import { Reveal, Eyebrow, MaskedLine } from "../components/Reveal";
import { IMAGES } from "../data/products";
import { BadgeCheck, Layers, Wind, Droplets, Ruler, Factory } from "lucide-react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const TIMELINE = [
  { year: "2008", title: "The First Factory", text: "Started our first unit — a 7,000 sq ft factory with a complete set of German-made machines. Fabrication of uPVC doors and windows begins." },
  { year: "2011", title: "First Showroom", text: "Opened our first showroom along with a sales office — customers could now touch, slide and lock every system before deciding." },
  { year: "2015", title: "Going Vertical", text: "Executed our first high-rise project — 23 floors of windows and doors, fabricated and installed by our own teams." },
  { year: "2019", title: "Scaling Up", text: "Expanded with a second 12,500 sq ft factory and a second complete German-made semi-automatic production line." },
  { year: "2023", title: "Experience, Redesigned", text: "Redesigned the experience centre and added new product series and colour options to the range." },
];

const PRINCIPLES = [
  { num: "01", title: "Measure Twice, Weld Once", text: "Every opening is site-verified by our own engineers before fabrication begins. We never fabricate from someone else's tape." },
  { num: "02", title: "No Compromise Cores", text: "Galvanized steel reinforcement in every sash and frame, no matter the size — even where nobody will ever see it." },
  { num: "03", title: "Service Is the Product", text: "A window is a 20-year relationship. Our own installation crews and on-call service keep every system performing — whenever you call, we come." },
];

const MATERIALS = [
  { icon: Layers, title: "Multi-Chamber Profiles", text: "4 and 5-chamber profiles trap insulating air pockets, cutting heat transfer and dampening sound." },
  { icon: BadgeCheck, title: "Galvanized Steel Core", text: "Up to 2.5 mm galvanized steel reinforcement resists wind loads up to 4.2 kPa without flex." },
  { icon: Droplets, title: "TPE Gaskets", text: "Co-extruded TPE seals stay elastic for decades — monsoon-proof, dust-proof, rattle-proof." },
  { icon: Wind, title: "Pressure-Equalized Drainage", text: "Hidden drainage chambers channel wind-driven rain out before it can ever reach the interior." },
];

const CERTS = ["ISO 9001:2008 Certified", "EN Compliant Profile Standards", "BIS Compliant Profile Standards"];

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="grain-overlay relative flex min-h-[70vh] items-end overflow-hidden" data-testid="about-hero">
        <img src="/images/about-hero.webp" alt="Wood-finish SOHOM window" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/60 to-obsidian/30" />
        <div className="relative mx-auto w-full max-w-7xl px-6 pb-20 pt-40 sm:px-10">
          <Eyebrow className="mb-6">About Us</Eyebrow>
          <h1 className="max-w-3xl font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.15}>We don't sell windows.</MaskedLine>
            <MaskedLine delay={0.32} className="text-gold">We engineer openings.</MaskedLine>
          </h1>
          <Reveal delay={0.6}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg">
              SOHOM is a fabrication-first uPVC windows and doors studio in Hyderabad. Every system that leaves our floor is cut, welded, reinforced and glazed by our own hands — never outsourced, never white-labelled.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-32" data-testid="about-timeline">
        <Reveal>
          <Eyebrow>The Journey</Eyebrow>
          <h2 className="mt-4 max-w-xl font-heading text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            Eighteen years of getting the details right.
          </h2>
        </Reveal>
        <div className="mt-16 grid gap-10 md:grid-cols-2 lg:grid-cols-5">
          {TIMELINE.map((t, i) => (
            <Reveal key={t.year} delay={i * 0.12}>
              <div className="border-t-2 border-gold/40 pt-6" data-testid={`timeline-${t.year}`}>
                <p className="font-mono text-sm font-bold tracking-[0.2em] text-gold">{t.year}</p>
                <h3 className="mt-3 font-heading text-lg font-semibold text-slate-100">{t.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{t.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-y border-border bg-surface/40" data-testid="about-principles">
        <div className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-32">
          <Reveal>
            <Eyebrow>How We Work</Eyebrow>
            <h2 className="mt-4 max-w-xl font-heading text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">Three rules on the factory floor.</h2>
          </Reveal>
          <div className="mt-16">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.06}>
                <div className={`group flex flex-col gap-4 border-t border-border py-10 transition-colors duration-500 hover:bg-gold/[0.03] sm:flex-row sm:items-baseline sm:gap-12 ${i === PRINCIPLES.length - 1 ? "border-b" : ""}`} data-testid={`principle-${p.num}`}>
                  <span className="text-stroke-gold font-heading text-6xl font-extrabold leading-none sm:text-7xl">{p.num}</span>
                  <h3 className="font-heading text-xl font-semibold text-slate-100 sm:w-72 sm:shrink-0 sm:text-2xl">{p.title}</h3>
                  <p className="max-w-2xl text-sm leading-relaxed text-slate-400 sm:text-base">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:py-32" data-testid="about-materials">
        <Reveal>
          <Eyebrow>Material Science</Eyebrow>
          <h2 className="mt-4 max-w-xl font-heading text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
            Inside every SOHOM frame.
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2">
          {MATERIALS.map((m, i) => (
            <Reveal key={m.title} delay={i * 0.1}>
              <div className="group h-full rounded-xl border border-border bg-carddark p-8 transition-all duration-500 hover:border-gold/40" data-testid={`material-${i}`}>
                <m.icon className="h-7 w-7 text-gold" strokeWidth={1.5} />
                <h3 className="mt-6 font-heading text-lg font-semibold text-slate-100 sm:text-xl">{m.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{m.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-14 flex flex-wrap items-center gap-4" data-testid="about-certifications">
            <Factory className="h-5 w-5 text-gold" />
            {CERTS.map((c) => (
              <span key={c} className="rounded-full border border-border bg-surface px-4 py-2 font-mono text-xs tracking-wide text-slate-300">
                {c}
              </span>
            ))}
          </div>
        </Reveal>
      </section>

      <section className="border-t border-border bg-surface/40" data-testid="about-cta">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center sm:px-10 lg:py-28">
          <Reveal>
            <h2 className="mx-auto max-w-2xl font-heading text-2xl font-bold tracking-tight text-slate-50 sm:text-3xl lg:text-4xl">
              Visit the experience centre. Feel the difference a weld makes.
            </h2>
            <Link to="/contact" data-testid="about-cta-button" className="group mt-10 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-semibold text-obsidian transition-colors duration-300 hover:bg-gold-hover">
              Plan a Visit
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
